import { useEffect } from 'react'
import { supabase, isDemoMode } from '@/lib/supabase'
import { hasAnalyticsConsent } from '@/components/CookieBanner'

const CRISP_SCRIPT_ID = 'crisp-chat-script'

function injectCrisp(websiteId: string) {
  if (!/^[a-f0-9-]+$/i.test(websiteId)) return
  if (document.getElementById(CRISP_SCRIPT_ID)) return

  window.$crisp = []
  window.CRISP_WEBSITE_ID = websiteId

  const script = document.createElement('script')
  script.id = CRISP_SCRIPT_ID
  script.async = true
  script.src = 'https://client.crisp.chat/l.js'
  document.head.appendChild(script)
}

function removeCrisp() {
  document.getElementById(CRISP_SCRIPT_ID)?.remove()
  delete window.CRISP_WEBSITE_ID
  delete window.$crisp
  // Remove Crisp injected elements
  document.querySelectorAll('[class*="crisp"]').forEach(el => el.remove())
}

/**
 * Loads Crisp live chat widget if enabled in platform_settings.
 * Respects cookie consent (functional cookies).
 *
 * platform_settings key: 'live_chat'
 * value: { enabled: boolean, provider: 'crisp', website_id: string }
 */
export function useCrispChat() {
  useEffect(() => {
    if (isDemoMode) return

    let cancelled = false

    const load = async () => {
      // Crisp is functional (not analytics), but we still respect consent
      if (!hasAnalyticsConsent()) return

      try {
        const { data } = await supabase
          .from('platform_settings')
          .select('value')
          .eq('key', 'live_chat')
          .single()

        if (cancelled) return

        if (data?.value) {
          const settings = data.value as { enabled: boolean; provider: string; website_id: string }
          if (settings.enabled && settings.provider === 'crisp' && settings.website_id) {
            injectCrisp(settings.website_id)
          }
        }
      } catch {
        // No live_chat setting → don't inject anything
      }
    }

    load()

    // React to consent changes
    const handleConsent = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail === 'accepted') load()
      else removeCrisp()
    }
    window.addEventListener('cookie-consent-changed', handleConsent)

    return () => {
      cancelled = true
      window.removeEventListener('cookie-consent-changed', handleConsent)
    }
  }, [])
}
